import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Ribbon } from "@/components/Layout";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend } from "recharts";
import { Euro, Wallet, Users, UserCheck, Activity, Search, Download } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { CHART_COLORS as COLORS, CHART_GRID, CHART_N1, EMERALD } from "@/lib/palette";
const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const REG_FILTERS = [
  { key: "toutes", label: "Toutes" },
  { key: "non_soldees", label: "Non soldées" },
  { key: "soldees", label: "Soldées" },
];
const REG_BADGE = {
  soldee: "bg-emerald-100 text-emerald-700",
  partielle: "bg-yellow-100 text-yellow-700",
  non_soldee: "bg-red-100 text-red-700",
};
const REG_LABEL = { soldee: "Soldée", partielle: "Partielle", non_soldee: "Non soldée" };
const PERIODE_LABEL = { semaine: "Cette semaine", mois: "Ce mois-ci", mois_dernier: "Mois dernier", trimestre: "Trimestre (90 j)", annee: "Année en cours", an_dernier: "L'an dernier" };

function AnnualTable({ title, rows, eur, compareRows }) {
  const tSoins = rows.reduce((a, m) => a + (m.soins || 0), 0);
  const tCa = rows.reduce((a, m) => a + m.ca, 0);
  const tPrevCa = compareRows ? compareRows.reduce((a, m) => a + m.ca, 0) : 0;
  const pct = (cur, prev) => (prev > 0 ? ((cur - prev) / prev) * 100 : null);
  const fmtPct = (v) => (v === null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(0)}%`);
  const pctColor = (v) => (v === null ? "text-slate-400" : v >= 0 ? "text-emerald-600" : "text-red-600");
  return (
    <div className="bg-white sx-card p-5">
      <h3 className="font-display font-semibold text-slate-900 mb-3">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-slate-500 text-xs uppercase"><th className="px-2 py-2 text-left font-semibold"></th>{rows.map((m) => <th key={m.mois} className="px-2 py-2 text-right font-semibold">{m.mois}</th>)}<th className="px-2 py-2 text-right">Total</th></tr></thead>
          <tbody>
            <tr className="border-t border-slate-100">
              <td className="px-2 py-2 text-left text-slate-500 font-medium whitespace-nowrap">Soins</td>
              {rows.map((m) => <td key={m.mois} className="px-2 py-2 text-right text-slate-600 whitespace-nowrap">{m.soins || "—"}</td>)}
              <td className="px-2 py-2 text-right font-bold text-slate-900 whitespace-nowrap">{tSoins}</td>
            </tr>
            <tr className="border-t border-slate-100">
              <td className="px-2 py-2 text-left text-slate-500 font-medium whitespace-nowrap">CA</td>
              {rows.map((m) => <td key={m.mois} className="px-2 py-2 text-right text-slate-700 whitespace-nowrap">{m.ca ? eur(m.ca) : "—"}</td>)}
              <td className="px-2 py-2 text-right font-bold text-slate-900 whitespace-nowrap">{eur(tCa)}</td>
            </tr>
            {compareRows && (
              <tr className="border-t border-slate-100">
                <td className="px-2 py-2 text-left text-slate-500 font-medium whitespace-nowrap" title="Écart du CA vs année précédente">Écart N-1</td>
                {rows.map((m, i) => { const v = pct(m.ca, (compareRows[i] || {}).ca || 0); return <td key={m.mois} className={`px-2 py-2 text-right text-xs whitespace-nowrap ${pctColor(v)}`}>{fmtPct(v)}</td>; })}
                <td className={`px-2 py-2 text-right text-xs font-bold whitespace-nowrap ${pctColor(pct(tCa, tPrevCa))}`}>{fmtPct(pct(tCa, tPrevCa))}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Piloter() {
  const nav = useNavigate();
  const [periode, setPeriode] = useState("mois");
  const [df, setDf] = useState("");
  const [dt, setDt] = useState("");
  const [stats, setStats] = useState(null);
  const [detail, setDetail] = useState(null); // {title, items}
  const [dq, setDq] = useState("");
  const [reg, setReg] = useState("toutes");
  const [sect, setSect] = useState("");

  const params = useMemo(() => ({ periode, date_from: df, date_to: dt }), [periode, df, dt]);

  const curLabel = () => periode === "perso"
    ? `Du ${df ? new Date(df).toLocaleDateString("fr-FR") : "…"} au ${dt ? new Date(dt).toLocaleDateString("fr-FR") : "aujourd'hui"}`
    : (PERIODE_LABEL[periode] || "");

  const rangeFor = () => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (periode === "perso") return { from: df || "2000-01-01", to: dt || iso(now) };
    let s = new Date(now);
    if (periode === "semaine") { const day = (now.getDay() + 6) % 7; s.setDate(now.getDate() - day); }
    else if (periode === "mois") s = new Date(now.getFullYear(), now.getMonth(), 1);
    else if (periode === "mois_dernier") { const first = new Date(now.getFullYear(), now.getMonth(), 1); return { from: iso(new Date(first.getFullYear(), first.getMonth() - 1, 1)), to: iso(first) }; }
    else if (periode === "trimestre") s.setDate(now.getDate() - 90);
    else if (periode === "annee") s = new Date(now.getFullYear(), 0, 1);
    else if (periode === "an_dernier") return { from: iso(new Date(now.getFullYear() - 1, 0, 1)), to: iso(new Date(now.getFullYear(), 0, 1)) };
    return { from: iso(s), to: iso(now) };
  };

  const toQuery = () => {
    if (["semaine", "mois", "annee"].includes(periode)) return `periode=${periode}`;
    const { from, to } = rangeFor();
    return `periode=perso&from=${from}&to=${to}`;
  };

  useEffect(() => {
    api.get("/dashboard/stats", { params }).then((r) => setStats(r.data)).catch(() => {});
  }, [params]);

  const openCaDetail = async (title, override, regDefault = "toutes", sub = "") => {
    const { data } = await api.get("/dashboard/factures", { params: override || params });
    setDq(""); setSect(""); setReg(regDefault); setDetail({ title, items: data, sub });
  };

  const secteursDispo = useMemo(() => {
    const s = new Set();
    (detail?.items || []).forEach((i) => (i.secteurs || []).forEach((x) => s.add(x)));
    return [...s].sort();
  }, [detail]);

  const filtered = useMemo(() => {
    const ql = dq.trim().toLowerCase();
    return (detail?.items || []).filter((i) => {
      if (reg === "non_soldees" && i.reglement === "soldee") return false;
      if (reg === "soldees" && i.reglement !== "soldee") return false;
      if (sect && !(i.secteurs || []).includes(sect)) return false;
      if (ql && !`${i.numero} ${i.patient_nom}`.toLowerCase().includes(ql)) return false;
      return true;
    });
  }, [detail, dq, reg, sect]);

  const totalTtc = filtered.reduce((a, i) => a + (i.montant_ttc || 0), 0);
  const totalReste = filtered.reduce((a, i) => a + (i.reste || 0), 0);

  const exportCsv = () => {
    const head = ["N°", "Date", "Patient", "TTC", "Réglé", "Reste", "Statut"];
    const lines = filtered.map((i) => [i.numero, i.date, i.patient_nom, i.montant_ttc, i.paye, i.reste, REG_LABEL[i.reglement] || ""]);
    const csv = [head, ...lines].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = "factures.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const kpi = (label, value, Icon, onClick, testid, priority = false) => (
    <button onClick={onClick} data-testid={testid || `kpi-${label}`}
      className="text-left sx-card p-4 transition-all"
      style={priority ? { borderColor: "oklch(0.9 0.06 85)", boxShadow: "inset 0 3px 0 var(--accent)" } : undefined}
      onMouseEnter={(e) => { if (!priority) e.currentTarget.style.boxShadow = "var(--sh-card-hover)"; }}
      onMouseLeave={(e) => { if (!priority) e.currentTarget.style.boxShadow = "none"; }}>
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-medium" style={{ color: "var(--muted-2)" }}>{label}</span>
        <Icon className="w-[15px] h-[15px]" style={{ color: "var(--muted-2)" }} />
      </div>
      <div className="mt-2 text-[24px] font-bold" style={{ color: "var(--ink)" }}>{value}</div>
    </button>
  );

  const delta = stats ? stats.ca_periode - stats.ca_periode_n1 : 0;

  return (
    <div>
      <Ribbon groups={[
        { name: "Période", children: (
          <div className="flex gap-1.5 items-center">
            <select data-testid="piloter-periode" value={periode} onChange={(e) => setPeriode(e.target.value)} className="px-2.5 py-2 rounded-lg border border-slate-300 text-sm">
              <option value="semaine">Cette semaine</option><option value="mois">Ce mois</option><option value="mois_dernier">Mois dernier</option>
              <option value="trimestre">Trimestre</option><option value="annee">Cette année</option><option value="an_dernier">L'an dernier</option><option value="perso">Période personnalisée</option>
            </select>
            {periode === "perso" && (
              <>
                <input type="date" data-testid="piloter-from" value={df} onChange={(e) => setDf(e.target.value)} className="px-2 py-2 rounded-lg border border-slate-300 text-sm" />
                <span className="text-slate-400 text-sm">→</span>
                <input type="date" data-testid="piloter-to" value={dt} onChange={(e) => setDt(e.target.value)} className="px-2 py-2 rounded-lg border border-slate-300 text-sm" />
              </>
            )}
          </div>) },
      ]} />

      {stats && (
        <>
          <div className="grid md:grid-cols-2 gap-3 mb-5">
            <div className="grid gap-3 content-start">
              {kpi("CA période", eur(stats.ca_periode), Euro, () => openCaDetail("Factures de la période", null, "toutes", curLabel()))}
              {kpi("Reste à encaisser", eur(stats.reste_a_encaisser), Wallet, () => openCaDetail("Factures non soldées", null, "non_soldees", curLabel()), "kpi-reste", true)}
              {kpi("Soins", (
                <span>
                  <span style={{ color: "var(--ink)" }}>{stats.nb_soins_faits}</span>
                  <span className="text-xs font-medium" style={{ color: "var(--muted)" }}> réalisés</span>
                  <span style={{ color: "var(--faint)" }}> · </span>
                  <span style={{ color: "var(--text-2)" }}>{stats.nb_soins_programmes}</span>
                  <span className="text-xs font-medium" style={{ color: "var(--muted)" }}> programmés</span>
                </span>
              ), Activity, () => nav(`/patients/soins?${toQuery()}`), "kpi-soins-mois")}
              {kpi("Patients actifs", stats.nb_patients_actifs ?? 0, UserCheck, () => nav(`/patients?${toQuery()}`), "kpi-patients-actifs")}
            </div>
            <div className="grid gap-3 content-start">
              {kpi("CA du mois", eur(stats.ca_mois), Euro, () => openCaDetail("Factures du mois", { periode: "mois", date_from: "", date_to: "" }, "toutes", "Ce mois-ci"))}
              {kpi("CA de l'année", eur(stats.ca_annee), Euro, () => openCaDetail("Factures de l'année", { periode: "annee", date_from: "", date_to: "" }, "toutes", "Année en cours"))}
              {kpi("Patients (total)", stats.nb_patients, Users, () => nav(`/patients?${toQuery()}`), "kpi-patients-total")}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mb-5">
            <div className="bg-white sx-card p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-semibold text-slate-900">Tendance du CA (période vs N-1)</h3>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${delta >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{delta >= 0 ? "+" : ""}{eur(delta)} vs N-1</span>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={stats.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
                  <XAxis dataKey="mois" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => eur(v)} /><Legend />
                  <Bar dataKey="ca_n1" name="N-1" fill={CHART_N1} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="ca" name="Période" fill={EMERALD} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white sx-card p-5">
              <h3 className="font-display font-semibold text-slate-900 mb-3">CA & soins par secteur</h3>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={stats.par_secteur} dataKey="ca" nameKey="secteur" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.secteur} (${e.soins})`}>
                    {stats.par_secteur.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v, n, p) => [`${eur(v)} · ${p.payload.soins} soin(s)`, p.payload.secteur]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white sx-card p-5 mb-5" data-testid="ca-par-categorie">
            <h3 className="font-display font-semibold text-slate-900 mb-3">CA par catégorie</h3>
            {(stats.par_categorie && stats.par_categorie.length > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={stats.par_categorie} dataKey="ca" nameKey="categorie" cx="50%" cy="50%" outerRadius={100} label={(e) => `${e.categorie} (${e.soins})`}>
                    {stats.par_categorie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v, n, p) => [`${eur(v)} · ${p.payload.soins} soin(s)`, p.payload.categorie]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-slate-400 py-8 text-center">Aucune facture avec catégorie sur la période.</p>}
          </div>

          <div className="space-y-4">
            <AnnualTable title={`CA mensuel — année en cours (${stats.annee ?? ""})`} rows={stats.annuel} eur={eur} compareRows={stats.annuel_prev} />
            {stats.annuel_prev && <AnnualTable title={`CA mensuel — année précédente (${stats.annee_prev ?? ""})`} rows={stats.annuel_prev} eur={eur} />}
          </div>
        </>
      )}

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl flex flex-col p-0" data-testid="factures-sheet">
          <SheetHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
            <SheetTitle>{detail?.title}</SheetTitle>
            <SheetDescription>{detail?.sub ? `${detail.sub} — cliquez une ligne pour ouvrir la fiche de soin.` : "Cliquez une ligne pour ouvrir la fiche de soin associée."}</SheetDescription>
          </SheetHeader>

          <div className="px-6 py-3 border-b border-slate-100 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {REG_FILTERS.map((f) => (
                <button key={f.key} data-testid={`fact-filter-${f.key}`} onClick={() => setReg(f.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${reg === f.key ? "bg-[#141821] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {f.label}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                {secteursDispo.length > 0 && (
                  <select data-testid="fact-secteur" value={sect} onChange={(e) => setSect(e.target.value)} className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm">
                    <option value="">Tous secteurs</option>
                    {secteursDispo.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
                <button data-testid="fact-export" onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50">
                  <Download className="w-4 h-4" /> CSV
                </button>
              </div>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
              <input data-testid="fact-search" value={dq} onChange={(e) => setDq(e.target.value)} placeholder="Rechercher n° ou patient…" className="pl-8 pr-3 py-2 w-full rounded-lg border border-slate-300 text-sm" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-3">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">N°</th><th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Patient</th><th className="text-right px-3 py-2">TTC</th>
                  <th className="text-right px-3 py-2">Réglé</th><th className="text-right px-3 py-2">Reste</th>
                  <th className="text-center px-3 py-2">Statut</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr key={i.id} data-testid={`fact-row-${i.id}`} className={`border-t border-slate-100 ${i.soin_id ? "hover:bg-emerald-50/40 cursor-pointer" : ""}`} onClick={() => i.soin_id && nav(`/fiche-soin/${i.soin_id}`)}>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{i.numero}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{i.date}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">{i.patient_nom || "—"}</td>
                    <td className="px-3 py-2 text-right font-medium whitespace-nowrap">{eur(i.montant_ttc)}</td>
                    <td className="px-3 py-2 text-right text-slate-600 whitespace-nowrap">{eur(i.paye)}</td>
                    <td className={`px-3 py-2 text-right whitespace-nowrap ${i.reste > 0 ? "text-red-600 font-medium" : "text-slate-400"}`}>{eur(i.reste)}</td>
                    <td className="px-3 py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-full ${REG_BADGE[i.reglement]}`}>{REG_LABEL[i.reglement]}</span></td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">Aucune facture.</td></tr>}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-200 font-semibold text-slate-900">
                    <td className="px-3 py-2" colSpan={3}>Total ({filtered.length})</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{eur(totalTtc)}</td>
                    <td className="px-3 py-2"></td>
                    <td className="px-3 py-2 text-right text-red-600 whitespace-nowrap">{eur(totalReste)}</td>
                    <td className="px-3 py-2"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
